import { makeStyles } from "tss-react/mui"
import { Box, Stack } from "@mui/material"
import { BillReceiversType } from "../../../../../modules/company/admin/accounting/bill-receivers/billReceiversTypes"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import {
    BillReceiverResponse,
    fetchBillReceiver,
    ReceiptConfirmOrderDialogQuery,
} from "../receiptConfirmOrderDialogApi"
import { BillReceiverTypeMS } from "../../../createOrder/createOrderTypes"
import {
    SectionBlock,
    SectionItemData,
    SectionItemLabel,
    SectionItemLink,
    SectionSubTitle,
} from "./common/ReceiptPrimitives"

const useStyles = makeStyles()((theme) => ({
    container: {
        background: theme.palette.tailwind.emerald[50],
    },
    wrap: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "1.5rem",
    },
    gridBox: {
        display: "grid",
        gridTemplateColumns: "9rem 1fr",
        gap: "1.5rem",
    },
}))

const transformData = (data: BillReceiverResponse) => {
    return data?.billReceiver
}

type BillReceiverProps = Readonly<{
    receiver: { id: number; name: string }
    costCodes?: { value: string; name: string; label?: string }[]
    userId: number
}>

export function BillReceiver({ receiver, costCodes, userId }: BillReceiverProps) {
    const { classes, cx } = useStyles()

    const { data: billReceiver } = useApiQuery(fetchBillReceiver, {
        queryName: ReceiptConfirmOrderDialogQuery.BILL_RECEIVER,
        dependencies: {
            userId,
            receiverId: receiver.id,
        },
        select: transformData,
    })
    if (!billReceiver) return null

    const renderContactInfo = () => {
        const contact = billReceiver.contactDetails
        if (!contact) return null

        return (
            <Box>
                {contact.email && (
                    <Stack direction="row" gap={1}>
                        <SectionItemData translationKey="confirm-order-dialog-bill-receiver-contact-email">
                            Email:
                        </SectionItemData>
                        <SectionItemLink href={`mailto:${contact.email}`}>{contact.email}</SectionItemLink>
                    </Stack>
                )}
                {contact.phoneNumber && <SectionItemData>T: {contact.phoneNumber}</SectionItemData>}
            </Box>
        )
    }

    const renderEndpoint = () => {
        const endpoint = billReceiver.endpoint
        if (!endpoint) return null

        if (endpoint.type === BillReceiverTypeMS.EAN_NUMBER && endpoint.ean) {
            return <SectionItemData>EAN: {endpoint.ean}</SectionItemData>
        }

        if (endpoint.type === BillReceiverTypeMS.EMAIL_ADDRESS && endpoint.email) {
            return <SectionItemLink href={`mailto:${endpoint.email}`}>{endpoint.email}</SectionItemLink>
        }

        return null
    }

    const renderByType = () => {
        switch (billReceiver.type) {
            case BillReceiversType.PRIVATE_COMPANY:
                return (
                    <>
                        {billReceiver.vatnumber && <SectionItemData>CVR: {billReceiver.vatnumber}</SectionItemData>}
                        {renderEndpoint()}
                        {renderContactInfo()}
                    </>
                )
            case BillReceiversType.PUBLIC_INSTITUTION:
                return (
                    <>
                        {renderEndpoint()}
                        {renderContactInfo()}
                    </>
                )
            case BillReceiversType.PRIVATE_PERSON:
                return (
                    <>
                        {renderEndpoint()}
                        {billReceiver.contactDetails?.phoneNumber && (
                            <SectionItemData>T: {billReceiver.contactDetails?.phoneNumber}</SectionItemData>
                        )}
                    </>
                )
            default:
                return null
        }
    }

    return (
        <SectionBlock
            classNames={classes.container}
            title="Bill receiver"
            translationKey="confirm-order-dialog-bill-receiver"
        >
            <Box className={classes.wrap}>
                <Stack gap="0.5rem">
                    <SectionSubTitle>{billReceiver.name}</SectionSubTitle>
                    {billReceiver.address?.displayName && (
                        <SectionItemLabel>{billReceiver.address?.displayName}</SectionItemLabel>
                    )}
                    {renderByType()}
                </Stack>

                {!!costCodes?.length && (
                    <Stack gap=".5rem">
                        <SectionSubTitle translationKey="confirm-order-dialog-bill-receiver-cost-centers">
                            Cost centers:
                        </SectionSubTitle>
                        {costCodes.map((costCode) => (
                            <Box className={classes.gridBox}>
                                <SectionItemLabel>{costCode.name}</SectionItemLabel>
                                <SectionItemData textAlign="right">
                                    {costCode.label ? `${costCode.value}, ${costCode.label}` : costCode.value}
                                </SectionItemData>
                            </Box>
                        ))}
                    </Stack>
                )}
            </Box>
        </SectionBlock>
    )
}
